import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  Shield,
  Zap,
  Key,
  Globe,
  ArrowRight,
  Terminal,
  Lock,
  Layers,
  ChevronRight,
  Database,
  BarChart3,
  Bot,
  Activity,
  GraduationCap,
  ShoppingCart,
  Store,
  Building2,
  Landmark,
  Leaf,
  Truck,
  HeartPulse,
  Plug,
} from 'lucide-react';
import { Container } from '../components/ui/Container';

const sections = [
  {
    id: 'getting-started',
    icon: Zap,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
    title: 'Démarrage rapide',
    description:
      'Intégrez l\'API Wanzo en quelques étapes : obtenez vos credentials Auth0, effectuez votre première requête et explorez les ressources disponibles.',
    steps: [
      'Créez un compte développeur sur la plateforme Wanzo.',
      'Récupérez votre Client ID et Client Secret depuis le tableau de bord.',
      'Implémentez le flux Auth0 PKCE pour obtenir un token d\'accès.',
      'Utilisez le token Bearer dans l\'en-tête `Authorization` de chaque requête.',
    ],
    code: `// Exemple : Authentification Auth0 PKCE
const tokenResponse = await fetch(
  'https://<AUTH0_DOMAIN>/oauth/token',
  {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      client_id: '<CLIENT_ID>',
      code_verifier: '<CODE_VERIFIER>',
      code: '<CODE>',
      redirect_uri: '<CALLBACK_URL>',
    }),
  }
);
const { access_token } = await tokenResponse.json();`,
  },
  {
    id: 'authentication',
    icon: Lock,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    title: 'Authentification & Sécurité',
    description:
      'Wanzo utilise Auth0 avec le flux PKCE (Proof Key for Code Exchange) pour une authentification sécurisée sans exposition des secrets client.',
    steps: [
      'Tous les endpoints protégés exigent un `Bearer Token` Auth0 valide.',
      'Les tokens ont une durée de vie limitée. Implémentez le refresh automatique.',
      'Utilisez HTTPS en production. Les requêtes HTTP sont rejetées.',
      'Les permissions sont vérifiées via les scopes Auth0 et les rôles utilisateur.',
    ],
    code: `// Exemple : En-tête d'autorisation
const response = await fetch(
  'https://api.wanzo.io/land/api/v1/users/me',
  {
    headers: {
      Authorization: \`Bearer \${access_token}\`,
      'Content-Type': 'application/json',
    },
  }
);`,
  },
  {
    id: 'endpoints',
    icon: Globe,
    color: 'text-green-500',
    bg: 'bg-green-50',
    title: 'Endpoints principaux',
    description:
      'L\'API est organisée en modules fonctionnels. Chaque module expose des endpoints REST standardisés.',
    steps: [
      '`GET /land/api/v1/users/me` : Profil de l\'utilisateur connecté.',
      '`GET /land/api/v1/companies` : Entreprises associées au compte.',
      '`GET /land/api/v1/subscriptions` : Abonnements actifs.',
      '`POST /land/api/v1/auth/token/refresh` : Rafraîchissement du token.',
    ],
    code: `// Exemple : Récupérer le profil utilisateur
const res = await fetch('/land/api/v1/users/me', {
  headers: { Authorization: \`Bearer \${token}\` },
});
const user = await res.json();
// { id, email, name, picture, roles, ... }`,
  },
  {
    id: 'errors',
    icon: Shield,
    color: 'text-red-500',
    bg: 'bg-red-50',
    title: 'Gestion des erreurs',
    description:
      'L\'API retourne des codes HTTP standard accompagnés d\'un corps JSON décrivant l\'erreur.',
    steps: [
      '`400 Bad Request` : Paramètres manquants ou invalides.',
      '`401 Unauthorized` : Token absent ou expiré.',
      '`403 Forbidden` : Permissions insuffisantes.',
      '`404 Not Found` : Ressource introuvable.',
      '`429 Too Many Requests` : Limite de débit dépassée.',
      '`500 Internal Server Error` : Erreur serveur, réessayez ultérieurement.',
    ],
    code: `// Structure d'une réponse d'erreur
{
  "error": "unauthorized",
  "message": "Token JWT invalide ou expiré",
  "statusCode": 401,
  "timestamp": "2026-03-23T10:00:00.000Z"
}`,
  },
  {
    id: 'sdks',
    icon: Layers,
    color: 'text-purple-500',
    bg: 'bg-purple-50',
    title: 'SDKs & Bibliothèques',
    description:
      'Des clients officiels et des exemples d\'intégration sont disponibles pour les principales technologies.',
    steps: [
      'JavaScript / TypeScript (React, Next.js, Node.js)',
      'Python (Flask, Django, FastAPI)',
      'REST pur, compatible avec tout langage HTTP.',
      'Postman Collection disponible sur demande.',
    ],
    code: `// Installation du client JS Wanzo (à venir)
npm install @wanzo/api-client

// Utilisation
import { WanzoClient } from '@wanzo/api-client';
const client = new WanzoClient({ accessToken: token });
const user = await client.users.me();`,
  },
  // ── NEW: Accounting API ──────────────────────────────────────────────────
  {
    id: 'accounting-api',
    icon: Database,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    title: 'API Comptabilité',
    description:
      'Accédez aux données comptables certifiées des PME : exercices, plan de comptes, grand livre, états financiers et import FEC. Toutes les réponses incluent une signature d\'intégrité blockchain.',
    steps: [
      '`GET /accounting/v1/exercises` : Liste des exercices comptables. Scope requis : `accounting:read`.',
      '`GET /accounting/v1/journal?from=&to=&type=` : Écritures du journal sur une période. Types : `purchase`, `sale`, `misc`.',
      '`GET /accounting/v1/ledger/:accountId` : Grand livre d\'un compte (débit/crédit/solde).',
      '`POST /accounting/v1/fec/import` : Import FEC (multipart/form-data). Formats : `.xlsx`, `.csv`.',
      '`GET /accounting/v1/statements?type=balance_sheet` : États financiers. Types : `balance_sheet`, `income_statement`, `cash_flow`.',
      '`GET /accounting/v1/accounts` : Plan comptable complet (numéro, libellé, classe).',
    ],
    code: `// Récupérer les écritures d'un mois
const res = await fetch(
  '/land/api/v1/accounting/v1/journal' +
  '?from=2025-01-01&to=2025-01-31&type=sale',
  { headers: { Authorization: \`Bearer \${token}\` } }
);
const { data, meta } = await res.json();
// data: [{ id, date, account, label, debit, credit, hash }]
// meta: { total, page, perPage }`,
  },
  // ── NEW: Credit Scoring API ──────────────────────────────────────────────
  {
    id: 'scoring-api',
    icon: BarChart3,
    color: 'text-green-600',
    bg: 'bg-green-50',
    title: 'API Credit Scoring (ACS)',
    description:
      'L\'Alternative Credit Scoring (ACS) Wanzo évalue les PME sur 5 piliers : Liquidité, Solvabilité, Stabilité, Activité, Intégrité. Les scores intègrent des données financières ET non-financières (Mobile Money, IoT, géolocalisation).',
    steps: [
      '`GET /scoring/v1/score/:companyId` : Score global ACS et détail des 5 piliers. Scope : `scoring:read`.',
      '`GET /scoring/v1/score/:companyId/history` : Historique des scores (évolution mensuelle).',
      '`GET /scoring/v1/score/:companyId/recommendations` : Actions recommandées pour améliorer le score.',
      '`POST /scoring/v1/score/:companyId/refresh` : Déclenche le recalcul du score avec les dernières données.',
      'Accès soumis à consentement explicite de la PME (scope `scoring:read` + accord utilisateur).',
      'Les scores sont calculés en temps réel à partir du Coffre-Fort Wanzo de la PME.',
    ],
    code: `// Récupérer le score ACS d'une PME
const res = await fetch(
  '/land/api/v1/scoring/v1/score/comp_abc123',
  { headers: { Authorization: \`Bearer \${token}\` } }
);
const score = await res.json();
/*
{
  "companyId": "comp_abc123",
  "globalScore": 72,
  "grade": "B+",
  "pillars": {
    "liquidity":   { "score": 68, "weight": 0.20 },
    "solvency":    { "score": 75, "weight": 0.25 },
    "stability":   { "score": 70, "weight": 0.20 },
    "activity":    { "score": 78, "weight": 0.20 },
    "integrity":   { "score": 69, "weight": 0.15 }
  },
  "updatedAt": "2025-06-01T08:00:00Z"
}
*/`,
  },
  // ── NEW: Adha IA API ─────────────────────────────────────────────────────
  {
    id: 'adha-api',
    icon: Bot,
    color: 'text-violet-500',
    bg: 'bg-violet-50',
    title: 'API Adha IA',
    description:
      'Intégrez l\'assistant IA Adha dans vos propres applications : envoyez des requêtes de contexte, déclenchez des automatisations et récupérez des recommandations en temps réel sans exposer les données sensibles.',
    steps: [
      '`POST /adha/v1/analyze` : Analyse de contexte (JSON payload). Scope : `adha:analyze`.',
      '`POST /adha/v1/automate` : Lance une automatisation (ex : envoi facture, relance). Scope : `adha:automate`.',
      '`GET /adha/v1/recommendations/:companyId` : Recommandations actionnables personnalisées.',
      'Les réponses sont streamées via Server-Sent Events (SSE) pour les analyses longues.',
      'Aucune donnée brute de la PME n\'est exposée dans les réponses (uniquement des insights).',
      'Rate limit : 100 req/min par token. Utilisez l\'en-tête `X-Request-Id` pour le débogage.',
    ],
    code: `// Analyse de contexte Adha
const res = await fetch('/land/api/v1/adha/v1/analyze', {
  method: 'POST',
  headers: {
    Authorization: \`Bearer \${token}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    companyId: 'comp_abc123',
    context: 'treasury',     // 'treasury' | 'credit' | 'sales' | 'risk'
    question: 'Quelles actions amélioreront ma trésorerie ce mois-ci ?',
  }),
});
const { answer, sources, confidence } = await res.json();`,
  },
  // ── NEW: Adha Claw (Agents) API ──────────────────────────────────────────
  {
    id: 'agents-api',
    icon: Zap,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
    title: 'API Adha Claw (Agents IA)',
    description:
      'Gérez les agents autonomes Adha Claw via l\'API : listez les agents disponibles, configurez des déclencheurs et suivez les exécutions. Idéal pour les intégrateurs low-code/no-code.',
    steps: [
      '`GET /agents/v1` : Liste des agents disponibles avec leurs capacités. Scope : `agents:read`.',
      '`GET /agents/v1/:agentId/config` : Configuration actuelle d\'un agent.',
      '`PUT /agents/v1/:agentId/config` : Mettre à jour la configuration (déclencheurs, règles, seuils).',
      '`POST /agents/v1/:agentId/trigger` : Déclencher manuellement une exécution d\'agent.',
      '`GET /agents/v1/:agentId/executions` : Historique des exécutions (statut, logs, outputs).',
      '`POST /agents/v1/webhooks` : S\'abonner aux événements d\'agent via webhook.',
    ],
    code: `// Déclencher l'agent de relance clients
const res = await fetch(
  '/land/api/v1/agents/v1/agent_relance_clients/trigger',
  {
    method: 'POST',
    headers: {
      Authorization: \`Bearer \${token}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      companyId: 'comp_abc123',
      params: { overdueThresholdDays: 14 },
    }),
  }
);
const { executionId, status, scheduledAt } = await res.json();`,
  },
  // ── NEW: Webhooks ────────────────────────────────────────────────────────
  {
    id: 'webhooks',
    icon: Activity,
    color: 'text-teal-500',
    bg: 'bg-teal-50',
    title: 'Webhooks & Événements',
    description:
      'Recevez des notifications en temps réel sur les événements Wanzo sans avoir à interroger l\'API (polling). Configurez une URL HTTPS et abonnez-vous aux types d\'événements pertinents.',
    steps: [
      '`POST /webhooks/v1` : Créer un abonnement webhook (url + events[]). Scope : `webhooks:manage`.',
      '`GET /webhooks/v1` : Lister vos abonnements actifs.',
      '`DELETE /webhooks/v1/:webhookId` : Supprimer un abonnement.',
      'Événements disponibles : `transaction.created`, `payment.received`, `score.updated`, `agent.executed`, `credit.approved`, `credit.rejected`.',
      'Chaque payload est signé avec un secret HMAC-SHA256. Vérifiez la signature avant de traiter.',
      'Réessais automatiques (3 tentatives) en cas d\'échec de livraison (HTTP 2xx requis).',
    ],
    code: `// Vérification de la signature webhook (Node.js)
import crypto from 'crypto';

function verifyWebhookSignature(
  payload: string,
  signature: string,   // X-Wanzo-Signature header
  secret: string       // votre webhook secret
): boolean {
  const expected = crypto
    .createHmac('sha256', secret)
    .update(payload)
    .digest('hex');
  return crypto.timingSafeEqual(
    Buffer.from(signature), Buffer.from(expected)
  );
}

// Payload exemple : score.updated
// {
//   "event": "score.updated",
//   "companyId": "comp_abc123",
//   "previousScore": 65,
//   "newScore": 72,
//   "timestamp": "2025-06-01T08:00:00Z"
// }`,
  },
];

const quickLinks = [
  { label: 'Guide de configuration', href: '#getting-started' },
  { label: 'Authentification Auth0', href: '#authentication' },
  { label: 'Endpoints REST', href: '#endpoints' },
  { label: 'Codes d\'erreur', href: '#errors' },
  { label: 'SDKs & outils', href: '#sdks' },
  { label: 'API Comptabilité', href: '#accounting-api' },
  { label: 'Credit Scoring (ACS)', href: '#scoring-api' },
  { label: 'API Adha IA', href: '#adha-api' },
  { label: 'API Adha Claw', href: '#agents-api' },
  { label: 'Webhooks & Événements', href: '#webhooks' },
];

// ─── Bandeau de restriction ─────────────────────────────────────────────────
function StickyGateBanner() {
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-t border-gray-700 px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-2xl">
      <div className="flex items-center gap-3">
        <span className="bg-primary/20 text-primary rounded-lg p-2 flex-shrink-0">
          <Lock className="w-5 h-5" />
        </span>
        <div>
          <p className="text-white font-semibold text-sm">Documentation complète réservée aux membres</p>
          <p className="text-gray-400 text-xs mt-0.5">Inscrivez-vous gratuitement pour accéder aux endpoints, exemples de code et guides d'intégration.</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/applications"
          className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-5 py-2.5 rounded-lg text-sm hover:bg-primary-hover transition-colors"
        >
          <Key className="w-4 h-4" />
          Créer un compte
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

export default function Developer() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className={`min-h-screen bg-gray-50 pt-24 ${scrolled ? 'pb-28' : 'pb-20'}`}>
      {/* Bandeau d'inscription : apparaît au scroll */}
      {scrolled && <StickyGateBanner />}
      <Container>
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-gray-500 flex items-center gap-2">
          <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
          <span>/</span>
          <span className="text-gray-800 font-medium">Développeur</span>
        </nav>

        {/*
          NB : on n'utilise PAS `position: sticky` ici parce que le Layout
          parent applique `overflow-x-hidden` (cf. Layout.tsx) ce qui casse
          sticky dans Chromium/WebKit. On utilise donc `position: fixed`
          aligné sur la Container (max-w-7xl centrée). L'aside réserve
          juste l'espace dans le grid pour que le main ne déborde pas.
        */}
        <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr] lg:gap-8">
          {/* Aside placeholder — réserve la colonne pour le nav fixe */}
          <aside className="hidden lg:block" aria-hidden="true" />

          {/* Nav fixe — visible uniquement sur lg+ */}
          <nav
            aria-label="Navigation de la documentation"
            className="hidden lg:flex lg:flex-col fixed top-24 w-64 bg-white rounded-xl border border-gray-200 shadow-sm p-5 z-20"
            style={{
              // Aligné sur le bord gauche du contenu de la Container max-w-7xl (80rem) avec px-lg (2rem).
              // max() garantit qu'on ne sort jamais à gauche quand le viewport est < 80rem+4rem.
              left: 'max(2rem, calc((100vw - 80rem) / 2 + 2rem))',
              // Empêche le nav de dépasser le viewport sur les écrans courts (laptop 13" en zoom).
              maxHeight: 'calc(100vh - 7rem)',
            }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
              Navigation
            </p>
            <ul className="space-y-1">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-primary transition-colors py-1.5 rounded"
                  >
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-gray-100">
              <div className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-gray-400" />
                <span className="text-xs text-gray-500">Endpoints disponibles après inscription</span>
              </div>
            </div>
          </nav>

          {/* Content */}
          <main className="lg:min-w-0 space-y-10">

            {/* Hero : aligné avec le contenu grâce au grid */}
            <div className="mb-4">
              {/* Badge opportunité */}
              <div className="flex items-center gap-3 mb-5">
                <span className="bg-primary/10 text-primary rounded-xl p-2.5">
                  <Plug className="w-6 h-6" />
                </span>
                <span className="inline-flex items-center gap-1.5 bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full border border-orange-200">
                  <Zap className="w-3 h-3" />
                  Opportunité partenaire développeur
                </span>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-5 leading-tight">
                Connectez votre application à l'écosystème Wanzo
              </h1>

              <div className="space-y-4 text-gray-600 text-base leading-relaxed mb-7">
                <p>
                  Vous développez une application de gestion pour des hôpitaux, écoles, supermarchés,
                  boutiques ou plateformes e-commerce&nbsp;? En intégrant l'API Wanzo, vous offrez
                  immédiatement à vos utilisateurs une <strong className="text-gray-800">comptabilité
                  certifiée SYSCOHADA tenue automatiquement</strong>, un accès à des{' '}
                  <strong className="text-gray-800">cabinets d'audit agréés</strong>,
                  l'<strong className="text-gray-800">authentification et la certification de leurs
                  données financières</strong>, ainsi qu'une{' '}
                  <strong className="text-gray-800">passerelle vers le financement et le crédit</strong>.
                  Vous enrichissez votre produit sans construire ces briques de zéro.
                </p>
                <p>
                  Vous êtes une <strong className="text-gray-800">institution financière</strong>&nbsp;?
                  L'API Wanzo expose une centrale de risque en temps réel, un moteur de{' '}
                  <strong className="text-gray-800">credit scoring alternatif (ACS)</strong> basé sur
                  des données financières ET comportementales, ainsi que des connecteurs prêts à
                  l'emploi pour l'<strong className="text-gray-800">intégration core banking</strong>.
                  Accélérez vos décisions de crédit sur les PME congolaises avec des données vérifiées
                  et signées numériquement.
                </p>
                <p>
                  Vous êtes une <strong className="text-gray-800">startup</strong> dans l'agritech,
                  la logistique, la santé ou l'éducation&nbsp;? Wanzo est votre infrastructure financière&nbsp;:
                  scoring de vos clients, comptabilité intégrée, accès au crédit et conformité
                  réglementaire sans détourner votre équipe de son cœur de métier.
                </p>
              </div>

              {/* Profils cibles */}
              <div className="mb-7">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Qui peut intégrer Wanzo&nbsp;?</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { icon: Building2,     label: 'Hôpitaux & Cliniques' },
                    { icon: GraduationCap, label: 'Écoles & Universités' },
                    { icon: ShoppingCart,  label: 'Supermarchés & Retail' },
                    { icon: Store,         label: 'Boutiques & E-commerce' },
                    { icon: Landmark,      label: 'Institutions financières' },
                    { icon: Leaf,          label: 'Agritech' },
                    { icon: Truck,         label: 'Logtech & Supply chain' },
                    { icon: HeartPulse,    label: 'Healthtech' },
                    { icon: Layers,        label: 'ERP & Gestion' },
                  ].map(({ icon: Icon, label }) => (
                    <span key={label} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                      <Icon className="w-3.5 h-3.5 text-primary" />
                      {label}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <a
                  href="#getting-started"
                  className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
                >
                  <BookOpen className="w-4 h-4" />
                  Démarrer l'intégration
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#endpoints"
                  className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors shadow-sm"
                >
                  <Terminal className="w-4 h-4" />
                  Référence API
                </a>
              </div>
            </div>
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <section
                  key={section.id}
                  id={section.id}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`rounded-lg p-2 ${section.bg}`}>
                        <Icon className={`w-5 h-5 ${section.color}`} />
                      </span>
                      <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                    </div>
                    <p className="text-sm text-gray-500 leading-relaxed">{section.description}</p>
                  </div>

                  {/* Corps de section : verrouillé au scroll */}
                  <div className="relative">
                    <div className={`p-6 grid grid-cols-1 md:grid-cols-2 gap-6 transition-all duration-300 ${scrolled ? 'blur-sm select-none pointer-events-none' : ''}`}>
                      {/* Steps */}
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                          Points clés
                        </p>
                        <ul className="space-y-2">
                          {section.steps.map((step, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="mt-0.5 flex-shrink-0 w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold">
                                {i + 1}
                              </span>
                              <span
                                className="text-sm text-gray-600 leading-relaxed"
                                dangerouslySetInnerHTML={{
                                  __html: step.replace(
                                    /`([^`]+)`/g,
                                    '<code class="bg-gray-100 px-1 rounded text-xs font-mono text-gray-800">$1</code>'
                                  ),
                                }}
                              />
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Code */}
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                          Exemple
                        </p>
                        <pre className="bg-gray-900 text-gray-100 rounded-lg p-4 text-[11px] leading-relaxed overflow-x-auto font-mono whitespace-pre-wrap">
                          {section.code}
                        </pre>
                      </div>
                    </div>

                    {/* Superposition de verrouillage */}
                    {scrolled && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-b-xl">
                        <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
                          <Lock className="w-4 h-4 text-gray-400" />
                          <span className="text-xs font-medium text-gray-500">Contenu réservé aux membres</span>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              );
            })}

            {/* Contact CTA */}
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-1">Besoin d'aide ?</h3>
                <p className="text-sm text-gray-500">
                  Notre équipe technique est disponible pour vous accompagner dans votre intégration.
                </p>
              </div>
              <Link
                to="/contact"
                className="flex-shrink-0 inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Nous contacter
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </main>
        </div>
      </Container>
    </div>
  );
}
